import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalError from './ModalError';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';

type ModalErrorContainerProps = {
    object: string;
    action: string;
    resetActionCallback?: ActionCreatorWithPayload<any>;
    resetActionCallbackParams?: any;
    errorSelector: Selector;
    pathToNavigateAfterError?: string;
    navigateToPrevPage?: boolean;
};

const ModalErrorContainer: FC<ModalErrorContainerProps> = ({
    object,
    action,
    resetActionCallback,
    resetActionCallbackParams,
    errorSelector,
    pathToNavigateAfterError,
    navigateToPrevPage,
}) => {
    const [open, setOpen] = useState(true);
    const dispatch = useDispatch();
    const errorInfo = useSelector(errorSelector);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleClose = () => {
        resetActionCallback && dispatch(resetActionCallback(resetActionCallbackParams));
        setOpen((open) => !open);

        if (navigateToPrevPage) {
            navigate(-1);
        } else if (pathToNavigateAfterError) {
            navigate(pathToNavigateAfterError);
        }
    };

    const getErrorReasonFromErrorObj = (errorInfo: any) => {
        function getErrorFromData(data: string | Object) {
            if (typeof data === 'object') {
                const errorReasons = Object.entries(data);

                return `${errorReasons
                    .reduce((accumulator, [key, value]) => accumulator + '&' + key + ': ' + value, '')
                    .split('&')
                    .join('\n')}`;
            }

            return data;
        }

        if (errorInfo?.payload) {
            if (
                errorInfo?.payload?.response?.data &&
                !errorInfo.payload.response.data.startsWith?.('<!DOCTYPE html>')
            ) {
                return getErrorFromData(errorInfo.payload.response.data);
            } else if (errorInfo?.payload?.message) {
                return errorInfo.payload.message;
            }
        } else if (errorInfo?.response?.data && !errorInfo.response.data.startsWith?.('<!DOCTYPE html>')) {
            return getErrorFromData(errorInfo.response.data);
        } else if (errorInfo?.message) {
            return errorInfo.message;
        }

        return t('unknown_error');
    };

    return (
        <ModalError
            object={object}
            action={action}
            open={open}
            errorInfo={errorInfo}
            handleClose={handleClose}
            getErrorReasonFromErrorObj={getErrorReasonFromErrorObj}
        />
    );
};

export default ModalErrorContainer;
